import { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
    Layout,
    Typography,
    Card,
    Flex,
    Spin,
    Alert,
} from "antd";
import {
    LocationPin2Icon,
    // Using generic icons or matching existing ones
} from "../../assets/CustomIcon";

import { getJwtPayload } from "../../utils/appUtils";
import PageLayout from "../../components/common/Layouts/PageLayout";
import HelmetWrapper from "../../components/common/Layouts/HelmetWrapper";
import ImageCardDisplay from "../../components/common/ImageCardDisplay";
// Import Venue Slice Actions and Selectors
import {
    fetchPublicVenues, // [ADDED] New action to fetch public venues (likely contains media)
    fetchVenueById,
    selectCurrentVenue,
    selectPublicVenues, // [ADDED] Selector for public venues list
    selectFetchPublicVenuesLoading, // [ADDED] Loading state for public venues
    selectFetchVenueByIdLoading,
    clearCurrentVenue
} from "../../redux/venueSlice";

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;

export default function VenueDetailsPage() {
    const { id } = useParams();
    const dispatch = useDispatch();
    const { state } = useLocation();

    // Venue fetch state (User's List - /my-venues)
    const allVenues = useSelector((state) => state.venue.venues);

    // [ADDED] Public Venue fetch state (All Venues - /venues) - Better chance of having media data
    const publicVenues = useSelector(selectPublicVenues);
    const loadingPublic = useSelector(selectFetchPublicVenuesLoading);

    // Specific venue fetch state
    const currentVenue = useSelector(selectCurrentVenue);
    const loadingSpecificVenue = useSelector(selectFetchVenueByIdLoading);

    // Combined state
    const [venue, setVenue] = useState(null);

    // Update local state when currentVenue (from API) loads
    // This useEffect ensures that if a more complete 'currentVenue' object (e.g., from fetchVenueById)
    // becomes available, it updates the local 'venue' state, especially if it contains media.
    useEffect(() => {
        if (currentVenue && String(currentVenue.id) === String(id)) {
            // Only override if currentVenue has more data (like media) or if we have nothing
            const hasMedia = currentVenue.media?.length > 0 || currentVenue.image || currentVenue.banner;
            if (hasMedia || !venue) {
                setVenue(currentVenue);
            }
        }
    }, [currentVenue, id, venue]);

    // Use Effect to load data
    // This useEffect orchestrates the data fetching strategy for venue details.
    // It prioritizes speed and completeness:
    // 1. Check `location.state` (fastest, from navigation).
    // 2. Check `publicVenues` list (likely to have media, from public API).
    // 3. Check `allVenues` list (user's specific venues, might lack media).
    // 4. Fallback to direct API calls (`fetchPublicVenues` and `fetchVenueById`).
    // If a venue is found but lacks media, it triggers `fetchVenueById` to get full details.
    useEffect(() => {
        // 1. Initial Load from State (Fastest)
        if (state?.venue) {
            setVenue(state.venue);

            // If the state venue looks incomplete (no media/images), fetch fresh details
            const hasMedia = state.venue.media?.length > 0 || state.venue.image || state.venue.banner;
            if (!hasMedia) {
                // [ADDED] Try grabbing from public list which might have the images if specific details fail
                if (!publicVenues || publicVenues.length === 0) dispatch(fetchPublicVenues());
                dispatch(fetchVenueById(id));
            }
            return;
        }

        // 2. [ADDED] Try to find in Public Venues list (Priority over my-venues for detail view if it has more data)
        // Public venues are generally more likely to contain complete media information.
        if (publicVenues && Array.isArray(publicVenues)) {
            const foundVenue = publicVenues.find(v => String(v.id) === String(id));
            if (foundVenue) {
                setVenue(foundVenue);
                // Check if we need to fetch specific anyway if the public list item is incomplete
                const hasMedia = foundVenue.media?.length > 0 || foundVenue.image || foundVenue.banner;
                if (!hasMedia) {
                    dispatch(fetchVenueById(id));
                }
                return;
            }
        }

        // 3. Fallback to My Venues list (if user is creator)
        // This list might be less complete in terms of media compared to public venues.
        if (allVenues && Array.isArray(allVenues)) {
            const foundVenue = allVenues.find(v => String(v.id) === String(id));
            if (foundVenue) {
                setVenue(foundVenue);
                const hasMedia = foundVenue.media?.length > 0 || foundVenue.image || foundVenue.banner;
                if (!hasMedia) {
                    // [ADDED] Fallback to public venues if my-venues is also missing media
                    if (!publicVenues || publicVenues.length === 0) dispatch(fetchPublicVenues());
                    dispatch(fetchVenueById(id));
                }
                return;
            }
        }

        // 4. Final Fallback: If not found in any local state/list, fetch directly.
        // Fetch public venues to populate the list for future checks and fetch specific details.
        dispatch(fetchPublicVenues()); // [ADDED] Fetch all venues (likely contains the images)
        dispatch(fetchVenueById(id));

        return () => {
            dispatch(clearCurrentVenue());
        };
    }, [dispatch, id, state, publicVenues, allVenues]); // Added publicVenues and allVenues to dependencies

    const loading = (loadingPublic && !venue) || (loadingSpecificVenue && !venue);


    if (loading) {
        return (
            <PageLayout>
                <Content style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '400px' }}>
                    <Spin size="large" tip="Loading venue details..." />
                </Content>
            </PageLayout>
        );
    }

    if (!venue) {
        return (
            <PageLayout>
                <Content style={{ padding: '24px' }}>
                    <Alert message="Venue not found" type="warning" showIcon />
                </Content>
            </PageLayout>
        );
    }

    // --- DATA EXTRACTION ---

    const venueName = venue.name || "Venue Name";

    // Helper to safely extract string from possible object (e.g. City/State objects)
    const safeStr = (val) => {
        if (!val) return null;
        if (typeof val === 'string') return val;
        // If it's an object, try to find a name property, otherwise fallback
        if (typeof val === 'object') return val.name || val.label || val.value || "";
        return String(val);
    };

    // Construct full address
    const addressParts = [
        safeStr(venue.address_line1),
        safeStr(venue.address_line2),
        safeStr(venue.city),
        safeStr(venue.state),
        safeStr(venue.zip_code)
    ].filter(Boolean);
    const fullAddress = addressParts.length > 0 ? addressParts.join(", ") : (venue.address || "Address not available");

    const token = localStorage.getItem('authToken');
    const userPayload = getJwtPayload(token);
    const capacity = venue.capacity ? `${venue.capacity} People` : "Capacity N/A";
    const manager = userPayload?.first_name + " " + userPayload?.last_name || "Manager N/A";
    const description = venue.description || "No description available.";

    return (
        <PageLayout>
            {/* SEO HELMET WRAPPER */}
            <HelmetWrapper
                title="Pinginvite - The Go-To Platform for Local Events in Kolkata & Dubai" 
                description="Pingvite connects people with local events in Kolkata and Dubai through community focus, affordability, 
                and city-specific visibility for organizers and users." 
                keywords="Top Upcoming Events in Kolkata, Best Live Events in Kolkata, best upcoming events in Kolkata, 
                online events near you in Kolkata,  upcoming events in Kolkata, Events in Kolkata, Top Upcoming Events in Dubai, 
                Best Live Events in Dubai, best upcoming events in Dubai, online events near you in Dubai, Event calendar in Dubai, 
                Event tickets in Dubai, upcoming events in Dubai, Events in Dubai"
            />
            <Content>
                {/* Banner Image - Same Design as Event Details */}
                <Card
                    className="noPaddingCardBody"
                    style={{ borderRadius: 12, overflow: "hidden", padding: 0 }}
                >
                  <ImageCardDisplay cardElement={venue} />
                </Card>

                {/* Venue Header - Same Design */}
                <div className="event-header">
                    <Title level={2} strong className="event-title">
                        {venueName}
                    </Title>
                    <div className="event-title-underline" />
                    <Flex gap={24} justify="space-between" wrap="wrap">
                        <Flex vertical gap={16} className="event-meta">

                            {/* Manager / Usage Type */}
                            <Text className="event-meta-item">
                                <span className="event-icon event-icon-circle gradient-icon"></span>
                                <strong>{manager}</strong>
                            </Text>

                            {/* Location */}
                            <Text className="event-meta-item">
                                <span className="event-icon">
                                    <LocationPin2Icon />
                                </span>
                                {fullAddress}
                            </Text>

                            {/* Capacity (Reusing generic icon or Calendar icon for list style) */}
                            <Text className="event-meta-item">
                                <span className="event-icon" style={{ fontWeight: 'bold' }}>
                                    {/* Using a simple text label or icon substitute */}
                                    👥
                                </span>
                                Capacity: {capacity}
                            </Text>
                        </Flex>
                    </Flex>
                </div>

                {/* Description Section */}
                <section className="eventsSec">
                    <div className="innerSec">
                        <Title level={4}>Description</Title>
                        <Paragraph>
                            {description}
                        </Paragraph>
                    </div>

                    {/* Tags Section (Replaces Tickets) */}
                    {venue.tags && (
                        <div className="innerSec">
                            <Title level={4}>Tags</Title>
                            <Paragraph>{venue.tags}</Paragraph>
                        </div>
                    )}
                </section>
            </Content>
        </PageLayout>
    );
}